import { Node, ReactNodeViewRenderer, mergeAttributes } from '@tiptap/react';

import { IntranetInlineImageView } from '@/products/intranet/components/intranet-image-frame';

/** Shown in the editor until the scan is CLEAN or SKIPPED. */
export const INTRANET_FILE_CHECK_HINT = 'Datei wird geprüft';

export type IntranetMediaRole = 'image' | 'gif' | 'video' | 'file';

export type IntranetAttachedFile = {
  id: string;
  filename: string;
  mimeType: string;
  scanStatus: 'PENDING' | 'CLEAN' | 'SKIPPED' | 'BLOCKED';
};

/** Stored src. The sanitiser only keeps paths under `/api/intranet/files`. */
export function intranetStoredFileSrc(fileId: string): string {
  return `/api/intranet/files/${fileId}/download`;
}

/**
 * Copy the selection, then clear the input so the same file can be chosen again.
 * A live FileList is emptied by clearing the input, so the copy has to come first.
 */
export function takePickedFiles(list: FileList | null, input: { value: string }): File[] {
  const files = list ? Array.from(list) : [];
  input.value = '';
  return files;
}

export const IntranetInlineImage = Node.create({
  name: 'intranetImage',
  group: 'block',
  atom: true,
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: '' },
    };
  },
  parseHTML() {
    return [{ tag: 'img[src]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(IntranetInlineImageView);
  },
});

export const IntranetInlineVideo = Node.create({
  name: 'intranetVideo',
  group: 'block',
  atom: true,
  addAttributes() {
    return {
      src: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'video[src]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['video', mergeAttributes(HTMLAttributes, { controls: '' })];
  },
});

export const IntranetPendingFile = Node.create({
  name: 'intranetPendingFile',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return {
      fileId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-intranet-file'),
        renderHTML: (attributes) =>
          attributes.fileId ? { 'data-intranet-file': attributes.fileId as string } : {},
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-intranet-file]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), INTRANET_FILE_CHECK_HINT];
  },
});
